<template>
    <div class="row">
        <div class="col-md-12">
            <div class="card stats-bar">
                <div class="stats-container">
                    <!-- Total de Eventos (não clicável) -->
                    <div class="stat-item stat-total">
                        <h4 class="stat-label">{{ $t('str.events.total') }}:</h4>
                        <label class="stat-value">{{ totalEvents }}</label>
                    </div>

                    <!-- Eventos Atendidos (clicável) -->
                    <div
                        class="stat-item stat-attended stat-clickable"
                        :class="{ 'stat-active': activeTypeFilter === 'ATTENDED' }"
                        @click="onFilterClick('ATTENDED')"
                        :title="$t('str.events.filter.click.hint')"
                    >
                        <span class="stat-label">{{ $t('str.events.attended') }}:</span>
                        <span class="stat-value">{{ attendedEvents }}</span>
                        <i v-if="activeTypeFilter === 'ATTENDED'" class="fa fa-check-circle stat-active-icon"></i>
                    </div>

                    <div
                        v-for="item in eventTypeStats"
                        :key="item.type"
                        class="stat-item stat-clickable"
                        :class="[item.className, { 'stat-active': activeTypeFilter === item.type }]"
                        @click="onFilterClick(item.type)"
                        :title="$t('str.events.filter.click.hint')"
                    >
                        <span class="stat-label">{{ $t(item.label) }}:</span>
                        <span class="stat-value">{{ item.value }}</span>
                        <i v-if="activeTypeFilter === item.type" class="fa fa-check-circle stat-active-icon"></i>
                    </div>
                </div>
                <div v-if="showEmptyEventTypeHint" class="stats-empty-hint">
                    {{ $t('str.settings.monitor.event.filters.empty.info') }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const EVENT_STAT_DEFINITIONS = [
    {
        type: 'SOS_ALERT',
        label: 'str.events.sos',
        countProp: 'sosEvents',
        className: 'stat-sos',
    },
    {
        type: 'INCIDENT',
        label: 'str.events.incidents',
        countProp: 'incidentEvents',
        className: 'stat-incident',
    },
    {
        type: 'CANCEL_PATROL',
        label: 'str.events.cancel_patrol',
        countProp: 'cancelPatrolEvents',
        className: 'stat-cancel-patrol',
    },
    {
        type: 'FAILURE_PATROL',
        label: 'str.events.failure_patrol',
        countProp: 'failurePatrolEvents',
        className: 'stat-failure-patrol',
    },
]

export default {
    name: 'StatsBar',
    props: {
        totalEvents: {
            type: Number,
            default: 0,
        },
        attendedEvents: {
            type: Number,
            default: 0,
        },
        sosEvents: {
            type: Number,
            default: 0,
        },
        incidentEvents: {
            type: Number,
            default: 0,
        },
        cancelPatrolEvents: {
            type: Number,
            default: 0,
        },
        failurePatrolEvents: {
            type: Number,
            default: 0,
        },
        enabledMonitorEventTypes: {
            type: Array,
            default: () => [],
        },
        isLoadingEventTypes: {
            type: Boolean,
            default: false,
        },
        activeTypeFilter: {
            type: String,
            default: null,
        },
    },
    computed: {
        enabledMonitorEventTypeSet() {
            const enabledTypes = Array.isArray(this.enabledMonitorEventTypes)
                ? this.enabledMonitorEventTypes.filter(type => typeof type === 'string' && type.trim().length > 0)
                : []
            return new Set(enabledTypes)
        },
        eventTypeStats() {
            const enabledTypes = this.enabledMonitorEventTypeSet
            if (enabledTypes.size === 0) return []

            return EVENT_STAT_DEFINITIONS
                .filter(item => enabledTypes.has(item.type))
                .map(item => ({
                    ...item,
                    value: this[item.countProp] || 0,
                }))
        },
        showEmptyEventTypeHint() {
            return !this.isLoadingEventTypes && this.enabledMonitorEventTypeSet.size === 0
        },
    },
    methods: {
        onFilterClick(type) {
            this.$emit('filter-type-selected', type)
        },
    },
}
</script>

<style lang="scss" scoped>
.stats-bar {
    background: #2f3336;
    border: 1px solid #444;
    border-radius: 4px;
    padding: 0.125rem 0.125rem;
}

.stats-container {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 0.35rem;
}

.stat-item {
    display: flex;
    align-items: baseline;
    padding: 0 0.85rem;
    border-right: 1px solid #555;
    color: #fff;
}

.stat-item:last-child {
    border-right: none;
}

.stat-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #9fa6b2;
    margin-right: 0.25rem;
}

.stat-value {
    font-size: 1rem;
    font-weight: bold;
    color: #fff;
}

.stat-total .stat-value {
    color: #ffffff;
}

.stat-attended .stat-value {
    color: #4caf50;
}

.stat-sos .stat-value {
    color: #ff7d7d;
}

.stat-incident .stat-value {
    color: #ffc36b;
}

.stat-cancel-patrol .stat-value {
    color: #8cc4ff;
}

.stat-failure-patrol .stat-value {
    color: #ffac70;
}

/* Clickable filter styles */
.stat-clickable {
    cursor: pointer;
    border-radius: 4px;
    transition: background 0.15s ease, opacity 0.15s ease;
    padding-top: 0.2rem;
    padding-bottom: 0.2rem;
    gap: 0.25rem;
}

.stat-clickable:hover {
    background: rgba(255, 255, 255, 0.08);
    opacity: 0.9;
}

.stat-active {
    background: rgba(255, 255, 255, 0.13) !important;
    border-radius: 4px;
    box-shadow: 0 0 0 1.5px rgba(255, 255, 255, 0.3);
}

.stat-active .stat-label {
    color: #fff;
}

.stat-active-icon {
    font-size: 0.65rem;
    color: #a0e0a0;
    margin-left: 0.2rem;
    align-self: center;
}

.stats-empty-hint {
    margin-top: 0.35rem;
    font-size: 0.75rem;
    text-align: center;
    color: #aeb7c4;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    padding-top: 0.35rem;
}

@media (max-width: 992px) {
    .stat-item {
        border-right: none;
    }
}
</style>
